import React, {useRef} from 'react';
import {Box, Container, Divider, Popover, PopoverProps, Typography} from '@mui/material';
import Utils from '../../../../core/services/utils/utils';
import {CommitDateRangeArg,} from './types';
import DateRangeForm, {DateRangeFormProps} from './DateRangeForm';

export interface DateRangePopoverProps extends DateRangeFormProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    /** Fired when the popover requests to close (Cancel or backdrop click) */
    onClose?: () => void;
    anchorOrigin?: PopoverProps['anchorOrigin'];
    transformOrigin?: PopoverProps['transformOrigin'];
}

const DateRangePopover: React.FC<DateRangePopoverProps> = (props) => {
    const {open, anchorEl, onClose, anchorOrigin, transformOrigin, ...formProps} = props;
    const {onCommitted} = formProps;
    const popoverId = useRef(Utils.createUUID());

    /**
     * We need to handle the committed value here in order to close the popover after a selection is made in the form.
     */
    const handleCommitted = React.useCallback((value: CommitDateRangeArg | undefined) => {
        onCommitted?.(value);
        onClose?.();
    }, [onCommitted, onClose]);

    /**
     * When the popover requests to close (either by clicking outside or pressing Escape),
     * we simply invoke the onClose callback to notify the parent component.
     */
    const handleCancelled = React.useCallback(() => {
        onClose?.();
    }, [onClose]);

    return (
        <Popover
            id={open ? popoverId.current : undefined}
            elevation={8}
            open={open}
            onClose={onClose}
            anchorReference="anchorEl"
            anchorEl={anchorEl}
            anchorOrigin={anchorOrigin ?? {vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={transformOrigin ?? {vertical: 'top', horizontal: 'left'}}
        >
            <Container sx={{maxHeight: 500, overflowY: 'auto', position: 'relative'}} disableGutters>
                <Box sx={{position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1, display: 'flex', flexDirection: 'column', px: 2, pt: 2}}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1.5}}>
                        {
                            formProps.value && (formProps.mode === 'auto' ? !!formProps.value.type : formProps.mode === 'advanced' ? !!formProps.value.advanced : !!formProps.value.static)
                                ? 'Edit Date Range'
                                : 'Select Date Range'
                        }
                    </Typography>
                    <Divider orientation="horizontal" sx={{borderColor: 'divider', marginInline: -2}} flexItem/>
                </Box>
                <DateRangeForm
                    key={String(open)}
                    {...formProps}
                    onCommitted={handleCommitted}
                    onCancelled={handleCancelled}
                />
            </Container>
        </Popover>
    );
};

export default DateRangePopover;
